import { useMemo, useState } from "react";
import { TriangleAlert, Plus, Trash2, X, ShieldCheck, Check } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Section, FieldError } from "@/components/common";

import { drawMeasures } from "@/lib/measureDraw";

const MeasureEditor = ({ hazard, idx, proposals, error, onChange, onRemove }) => {
  const setMeasures = (measures) => onChange({ ...hazard, measures });
  const proposed = new Set(proposals.map((p) => p.text));
  const free = hazard.measures.map((m, mi) => ({ m, mi })).filter(({ m }) => !proposed.has(m));
  const toggle = (text) => setMeasures(hazard.measures.includes(text) ? hazard.measures.filter((x) => x !== text) : [...hazard.measures, text]);
  return (
    <div data-field={`hazards.${hazard.key}`} data-testid={`selected-hazard-${idx}`} className={`rounded-lg border p-3 sm:p-4 ${error ? "border-red-500/70 bg-red-950/20" : "border-amber-500/30 bg-background/40"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-data text-[11px] uppercase tracking-widest text-amber-400">{hazard.family_name}</p>
          {hazard.custom ? (
            <Input data-testid={`selected-hazard-${idx}-label`} value={hazard.label} onChange={(e) => onChange({ ...hazard, label: e.target.value })} className="mt-1 h-11" placeholder="Décrire le danger" />
          ) : (
            <p className="font-bold">{hazard.label}</p>
          )}
        </div>
        <button type="button" data-testid={`selected-hazard-${idx}-remove`} aria-label="Retirer ce danger" onClick={onRemove} className="flex h-11 w-11 items-center justify-center rounded-md text-slate-400 hover:bg-white/5"><X className="h-5 w-5" /></button>
      </div>
      <p className="mt-3 text-sm font-semibold">Mesures de maîtrise</p>
      {proposals.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`hazard-${idx}-proposals`}>
          {proposals.map((s) => {
            const on = hazard.measures.includes(s.text);
            return (
              <button key={s.id} type="button" aria-pressed={on} data-testid={`hazard-${idx}-suggest-${s.id}`} onClick={() => toggle(s.text)}
                className={`min-h-[40px] rounded-full border px-3 text-left text-xs font-semibold transition-colors ${on ? "border-amber-400 bg-amber-500/20 text-amber-100" : "border-border text-slate-300 hover:border-amber-500/60 hover:text-amber-200"}`}>
                {on ? <Check className="mr-1 inline h-3.5 w-3.5" /> : "+ "}{s.text}
              </button>
            );
          })}
        </div>
      )}
      <div className="mt-2 space-y-2">
        {free.map(({ m, mi }) => (
          <div key={mi} className="flex gap-2">
            <Textarea data-testid={`hazard-${idx}-measure-${mi}`} rows={2} value={m} placeholder="Ex. Couper l'alimentation électrique avant de brancher le matériel"
              onChange={(e) => setMeasures(hazard.measures.map((x, j) => (j === mi ? e.target.value : x)))} className="min-h-[52px] text-base" />
            <button type="button" aria-label="Supprimer la mesure" data-testid={`hazard-${idx}-measure-${mi}-remove`} onClick={() => setMeasures(hazard.measures.filter((_, j) => j !== mi))} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-white/5"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" data-testid={`hazard-${idx}-add-measure`} onClick={() => setMeasures([...hazard.measures, ""])}
          className="min-h-[36px] rounded-full border border-amber-500/50 bg-amber-500/10 px-3 text-xs font-semibold text-amber-200">+ Autre mesure libre</button>
      </div>
      <FieldError error={error} testId={`selected-hazard-${idx}-error`} />
    </div>
  );
};

export const Hazards = ({ families, measures = [], links = {}, seed = "", hazards, noHazard, errors, onHazards, onNoHazard }) => {
  const [custom, setCustom] = useState("");
  const draws = useMemo(() => {
    const byHazard = {};
    measures.forEach((m) => { (byHazard[m.hazard_id] ||= []).push(m); });
    const ids = new Set([...Object.keys(byHazard), ...Object.keys(links)]);
    return Object.fromEntries([...ids].map((hid) => {
      const pool = [...(byHazard[hid] || []), ...(links[hid] || []).flatMap((x) => byHazard[x] || [])];
      return [hid, drawMeasures(pool.filter((m, i) => pool.findIndex((y) => y.id === m.id) === i), `${seed}:${hid}`)];
    }));
  }, [measures, links, seed]);
  const selectedKeys = new Set(hazards.map((h) => h.key));
  const toggle = (fam, hz) => {
    if (selectedKeys.has(hz.id)) onHazards(hazards.filter((h) => h.key !== hz.id));
    else onHazards([...hazards, { key: hz.id, family_id: fam.id, family_name: fam.name, label: hz.label, custom: false, measures: [] }]);
  };
  const addCustom = () => {
    onHazards([...hazards, { key: `custom-${Date.now()}`, family_id: null, family_name: "Danger hors référentiel", label: custom, custom: true, measures: [] }]);
    setCustom("");
  };

  return (
    <Section title="Dangers et mesures" icon={TriangleAlert} testId="take5-hazards-section" field="hazards" error={errors.hazards}>
      <FieldError error={errors.hazards} testId="hazards-error" />
      <Accordion type="multiple" className="mt-2 rounded-lg border border-border">
        {families.map((fam) => {
          const count = fam.hazards.filter((h) => selectedKeys.has(h.id)).length;
          return (
            <AccordionItem key={fam.id} value={fam.id} className="border-border px-3">
              <AccordionTrigger data-testid={`family-${fam.name}`} className="min-h-[52px] text-base font-semibold hover:no-underline">
                <span className="flex items-center gap-2">{fam.name}{count > 0 && <span className="rounded-full bg-amber-500 px-2 py-0.5 font-data text-xs text-slate-950">{count}</span>}</span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-wrap gap-2 pb-2">
                  {fam.hazards.map((hz) => {
                    const on = selectedKeys.has(hz.id);
                    return (
                      <button key={hz.id} type="button" aria-pressed={on} data-testid={`hazard-option-${hz.label}`} onClick={() => toggle(fam, hz)}
                        className={`min-h-[44px] rounded-md border px-3 text-sm font-medium transition-colors ${on ? "border-amber-400 bg-amber-500/20 text-amber-100" : "border-border bg-background text-slate-300 hover:border-slate-500"}`}>
                        {on ? "✓ " : ""}{hz.label}
                      </button>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
      <div className="mt-4 flex gap-2">
        <Input data-testid="custom-hazard-input" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Autre danger non listé…" className="h-12 text-base" />
        <Button type="button" data-testid="custom-hazard-add" variant="secondary" disabled={!custom.trim()} onClick={addCustom} className="h-12"><Plus className="mr-1 h-4 w-4" /> Ajouter</Button>
      </div>
      {hazards.length > 0 && (
        <div className="mt-5 space-y-3">
          <p className="font-data text-xs uppercase tracking-widest text-muted-foreground">Dangers retenus ({hazards.length})</p>
          {hazards.map((h, idx) => (
            <MeasureEditor key={h.key} hazard={h} idx={idx} proposals={h.custom ? [] : draws[h.key] || []} error={errors[`hazards.${h.key}`]}
              onChange={(nh) => onHazards(hazards.map((x) => (x.key === h.key ? nh : x)))}
              onRemove={() => onHazards(hazards.filter((x) => x.key !== h.key))} />
          ))}
        </div>
      )}
      <label data-field="no_hazard_confirmed" className={`mt-5 flex min-h-[52px] cursor-pointer items-center gap-3 rounded-lg border p-3 ${noHazard ? "border-emerald-400/60 bg-emerald-950/40" : "border-border"} ${hazards.length ? "opacity-60" : ""}`}>
        <input type="checkbox" data-testid="no-hazard-checkbox" className="h-5 w-5 accent-amber-500" checked={noHazard} onChange={(e) => onNoHazard(e.target.checked)} />
        <ShieldCheck className="h-5 w-5 text-emerald-300" />
        <span className="text-sm font-semibold">Je confirme qu'aucun danger n'a été identifié pour cette tâche</span>
      </label>
      <FieldError error={errors.no_hazard_confirmed} testId="no-hazard-error" />
    </Section>
  );
};
